import React, { useContext, useState, useEffect } from 'react';
import { APP_VERSION } from '../config';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { AuthContext, API } from '@/App';
import FeedbackButton from './FeedbackButton';
import axios from 'axios';
import { 
  Home, 
  Calculator, 
  AlertTriangle, 
  BookOpen, 
  ShoppingBag, 
  User, 
  LogOut, 
  Menu, 
  X, 
  Wrench,
  Settings2,
  Shield,
  Compass,
  Target,
  Bell,
  Printer,
  Thermometer,
  Layers,
  Box,
  ArrowLeft,
  Users,
  Zap
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Check if user is admin using backend flags only
const checkIsAdmin = (user) => {
  if (!user) return false;
  return !!(user.is_admin || user.is_super_admin);
};

// Determine current discipline from URL path
const getDisciplineFromPath = (pathname) => {
  if (pathname.startsWith('/3d-printing')) return '3d_printing';
  if (pathname.startsWith('/laser')) return 'laser_cutting';
  return 'cnc_routing';
};

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const token = localStorage.getItem('token');
  
  // Determine current discipline from URL
  const currentDiscipline = getDisciplineFromPath(location.pathname);
  const is3DPrinting = currentDiscipline === '3d_printing';
  const isLaserCutting = currentDiscipline === 'laser_cutting';
  
  // Check if current user is admin (using robust check)
  const isAdmin = checkIsAdmin(user);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data.unread || 0);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };
    
    fetchNotificationCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchNotificationCount, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // ============================================
  // 3D PRINTING NAVIGATION (Completely Separate)
  // ============================================
  const printing3DSetupItems = [
    {
      path: '/3d-printing/introduction',
      label: 'Step by Step Guidance',
      icon: Target,
      tooltip: 'Learn how to use 3D Printing Helpdesk effectively',
      highlight: true
    },
    {
      path: '/3d-printing/setup',
      label: 'Printer Setup',
      icon: Printer,
      tooltip: 'Configure your 3D printer profile'
    },
    {
      path: '/3d-printing/my-equipment',
      label: 'My Equipment',
      icon: Settings2,
      tooltip: 'View and manage your saved printer profiles'
    },
    {
      path: '/3d-printing/marketplace',
      label: 'Marketplace',
      icon: ShoppingBag,
      tooltip: 'Buy and sell 3D printing equipment and supplies'
    },
  ];

  const printing3DNavItems = [
    {
      path: '/3d-printing',
      label: 'Dashboard',
      icon: Home,
      tooltip: 'Your 3D Printing command center'
    },
    {
      path: '/3d-printing/calculator',
      label: 'Print Calculator',
      icon: Calculator,
      tooltip: 'Get optimal temperature, speed, and retraction settings'
    },
    {
      path: '/3d-printing/troubleshooting',
      label: 'Troubleshooting',
      icon: AlertTriangle,
      tooltip: 'Fix stringing, warping, adhesion and more'
    },
    {
      path: '/3d-printing/project-picker',
      label: 'Project Picker',
      icon: Target,
      tooltip: 'Get material and settings recommendations for your project type'
    },
    {
      path: '/3d-printing/my-tools',
      label: 'My Tools',
      icon: Wrench,
      tooltip: 'Manage your personal 3D printing tool library'
    },
    {
      path: '/3d-printing/materials',
      label: 'Guides & Tips',
      icon: BookOpen,
      tooltip: 'Learn about PLA, PETG, ABS and more',
      children: [
        { path: '/3d-printing/knowledge', label: 'Knowledge Base', icon: BookOpen, tooltip: 'Articles on calibration, technique, safety, and troubleshooting' },
      ]
    },
    {
      path: '/3d-printing/methodology',
      label: 'Methodology',
      icon: Compass,
      tooltip: 'How 3D Printing Helpdesk approaches recommendations'
    },
    {
      path: '/3d-printing/community-settings',
      label: 'Community Settings',
      icon: Users,
      tooltip: 'Proven settings shared by the community',
      highlight: true
    },
  ];

  // ============================================
  // CNC ROUTING NAVIGATION (Original)
  // ============================================
  const cncSetupItems = [
    { 
      path: '/introduction', 
      label: 'Step by Step Guidance', 
      icon: Target,
      tooltip: 'Learn how to use CNC-Helpdesk effectively',
      highlight: true
    },
    { 
      path: '/setup-wizard', 
      label: 'Machine Setup', 
      icon: Compass,
      tooltip: 'Guided setup to configure your CNC machine step-by-step'
    },
    { 
      path: '/settings/machine-setup', 
      label: 'My Equipment', 
      icon: Settings2,
      tooltip: 'Set machine + spindle profiles for better recommendations'
    },
    { 
      path: '/marketplace', 
      label: 'Marketplace', 
      icon: ShoppingBag,
      tooltip: 'Browse and share CNC resources'
    },
  ];

  const cncNavItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: Home,
      tooltip: 'Your CNC command center'
    },
    { 
      path: '/calculator', 
      label: 'Calculator', 
      icon: Calculator,
      tooltip: 'Get safe feeds, speeds, depth & export-ready settings'
    },
    { 
      path: '/troubleshooting', 
      label: 'Troubleshooting', 
      icon: AlertTriangle,
      tooltip: 'Diagnose and fix common CNC issues'
    },
    { 
      path: '/tools/quick-pick', 
      label: 'Project Picker', 
      icon: Target,
      tooltip: 'Get material & tool recommendations for your project type'
    },
    { 
      path: '/tools', 
      label: 'My Tools', 
      icon: Wrench,
      tooltip: 'Manage your personal tool library'
    },
    {
      path: '/knowledge',
      label: 'Guides & Tips',
      icon: BookOpen,
      tooltip: 'Learn CNC best practices and techniques',
      children: [
        { path: '/knowledge', label: 'Knowledge Base', icon: BookOpen, tooltip: 'Articles on feeds & speeds, workholding, materials, and more' },
      ]
    },
    { 
      path: '/methodology', 
      label: 'Methodology', 
      icon: Compass,
      tooltip: 'How CNC-Helpdesk approaches recommendations'
    },
    { 
      path: '/community-settings', 
      label: 'Community Settings', 
      icon: Users,
      tooltip: 'Proven settings shared by the community',
      highlight: true
    },
  ];

  // ============================================
  // LASER CUTTING NAVIGATION
  // ============================================
  const laserSetupItems = [
    {
      path: '/laser/introduction',
      label: 'Step by Step Guidance',
      icon: Target,
      tooltip: 'Learn the complete laser workflow',
      highlight: true
    },
    {
      path: '/laser/setup',
      label: 'Laser Setup',
      icon: Settings2,
      tooltip: 'Configure your CO2 or diode laser'
    },
    {
      path: '/laser/my-equipment',
      label: 'My Equipment',
      icon: Wrench,
      tooltip: 'View and manage your saved laser machine profiles'
    },
    {
      path: '/laser/marketplace',
      label: 'Marketplace',
      icon: ShoppingBag,
      tooltip: 'Buy and sell laser cutting equipment and materials'
    },
  ];

  const laserNavItems = [
    {
      path: '/laser',
      label: 'Dashboard',
      icon: Home,
      tooltip: 'Your laser command center'
    },
    {
      path: '/laser/calculator',
      label: 'Laser Calculator',
      icon: Calculator,
      tooltip: 'Get optimal power, speed, and pass settings'
    },
    {
      path: '/laser/troubleshooting',
      label: 'Troubleshooting',
      icon: AlertTriangle,
      tooltip: 'Fix alignment, power, and quality issues'
    },
    {
      path: '/laser/project-picker',
      label: 'Project Picker',
      icon: Target,
      tooltip: 'Get material and settings recommendations for your project type'
    },
    {
      path: '/laser/my-tools',
      label: 'My Tools',
      icon: Wrench,
      tooltip: 'Manage your personal laser cutting tool library'
    },
    {
      path: '/laser/materials',
      label: 'Guides & Tips',
      icon: BookOpen,
      tooltip: 'Settings and safety info for all materials',
      children: [
        { path: '/laser/safety', label: 'Safety Guide', icon: Shield, tooltip: 'Essential laser safety rules, PPE, and ventilation' },
        { path: '/laser/design-prep', label: 'Design File Prep', icon: Layers, tooltip: 'How to prepare SVG, DXF, and raster files for laser cutting' },
        { path: '/laser/software-guide', label: 'Software Guide', icon: Box, tooltip: 'LightBurn, xTool Creative Space, and LaserGRBL setup and tips' },
        { path: '/laser/machines', label: 'Machines Catalog', icon: Zap, tooltip: 'Diode, CO2, fibre, and galvo machine specs and comparisons' },
        { path: '/laser/knowledge', label: 'Knowledge Base', icon: BookOpen, tooltip: 'Articles on calibration, technique, maintenance, and troubleshooting' },
      ]
    },
    {
      path: '/laser/methodology',
      label: 'Methodology',
      icon: Compass,
      tooltip: 'How Laser Cutting Helpdesk approaches recommendations'
    },
    {
      path: '/laser/community-settings',
      label: 'Community Settings',
      icon: Users,
      tooltip: 'Proven settings shared by the community',
      highlight: true
    },
  ];

  // Select navigation based on discipline
  const getSetupNavItems = () => {
    if (is3DPrinting) return printing3DSetupItems;
    if (isLaserCutting) return laserSetupItems;
    return cncSetupItems;
  };
  
  const getMainNavItems = () => {
    if (is3DPrinting) return printing3DNavItems;
    if (isLaserCutting) return laserNavItems;
    return cncNavItems;
  };
  
  const setupNavItems = getSetupNavItems();
  const mainNavItems = getMainNavItems();

  // Get discipline title and color
  const getDisciplineTitle = () => {
    if (is3DPrinting) return '3D Printing Edition';
    if (isLaserCutting) return 'Laser Cutting Edition';
    return 'Router Edition';
  };
  const getDisciplineColor = () => {
    if (is3DPrinting) return 'text-orange-500 border-orange-500';
    if (isLaserCutting) return 'text-orange-500 border-orange-500';
    return 'text-[#e67e22] border-[#e67e22]';
  };

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      {/* Header - Large Centered Logo with User controls below */}
      <header className="sticky top-0 z-50 bg-[#1f1f1f] border-b border-[#3a3a3a]">
        <div className="flex flex-col items-center px-4 lg:px-6 py-1">
          {/* Mobile menu toggle - top left absolute */}
          <button 
            className="lg:hidden absolute left-4 top-2 p-2 text-[#a3a3a3] hover:text-white"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {/* Back to Home - top right */}
          <Link 
            to={isLaserCutting ? '/laser' : is3DPrinting ? '/3d-printing' : '/dashboard'} 
            className="absolute right-4 top-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#a3a3a3] hover:text-white hover:bg-[#2c2c2c] rounded transition-colors"
          >
            <Home className="w-3 h-3" />
            <span>Home</span>
          </Link>

          {/* Large Centered Logo */}
          <Link to={is3DPrinting ? '/3d-printing' : '/dashboard'} className="flex items-center -mb-2">
            <img
              src={require('@/assets/logo.png')}
              alt="CNC-Helpdesk logo"
              className="h-[10rem] md:h-[12rem] lg:h-[14rem] w-auto drop-shadow-lg"
              style={{ background: 'transparent' }}
            />
          </Link>
          
          {/* Discipline Badge */}
          <div className="flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider rounded border border-[#e67e22] text-[#e67e22] mt-0.5">
            {is3DPrinting ? <Printer className="w-3 h-3" /> : isLaserCutting ? <Zap className="w-3 h-3" /> : <Compass className="w-3 h-3" />}
            <span>{is3DPrinting ? '3D Printing' : isLaserCutting ? 'Laser Cutting' : 'CNC Router'}</span>
          </div>

          {/* User controls - centered below logo */}
          <div className="flex items-center gap-4 mt-1">
            <Link to={is3DPrinting ? "/3d-printing/profile" : isLaserCutting ? "/laser/profile" : "/profile"}>
              <button className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-[#a3a3a3] hover:text-white hover:bg-[#2c2c2c] rounded-sm transition-colors">
                <User className="w-4 h-4" />
                <span>{user?.full_name}</span>
              </button>
            </Link>
            {/* Notification Bell */}
            <Link to={is3DPrinting ? "/3d-printing/profile?tab=notifications" : isLaserCutting ? "/laser/profile?tab=notifications" : "/profile?tab=notifications"}>
              <button className="relative flex items-center gap-2 px-3 py-1 text-sm font-medium text-[#a3a3a3] hover:text-white hover:bg-[#2c2c2c] rounded-sm transition-colors">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </Link>
            <div className="flex items-center gap-2 px-2 py-1 text-sm">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full animate-pulse" />
              <span className="text-xs text-[#a3a3a3]">System Online</span>
            </div>
            <button 
              onClick={logout} 
              className="flex items-center gap-2 px-3 py-1 text-sm font-medium text-[#a3a3a3] hover:text-white hover:bg-[#2c2c2c] rounded-sm transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>

        {/* Status Bar */}
        <div className="h-1 bg-gradient-to-r from-[#e67e22]/20 via-[#e67e22]/40 to-[#e67e22]/20" />
      </header>

      <div className="flex relative">
        {/* Sidebar - Dark Industrial - Fixed on desktop */}
        <aside
          className={`fixed top-[15rem] left-0 z-40 h-[calc(100vh-15rem)] w-64 bg-[#1f1f1f] border-r border-[#3a3a3a] transition-transform duration-300 overflow-y-auto ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          {/* Navigation */}
          <TooltipProvider delayDuration={300}>
            <nav className="p-3 space-y-1">
              {/* Your Setup Section - TOP */}
              <div className="pb-2">
                <div className="flex items-center gap-2 px-3 mb-2">
                  <div className="flex-1 h-px bg-[#3a3a3a]" />
                  <span className="text-[10px] uppercase tracking-wider text-[#737373] font-medium">
                    {is3DPrinting ? 'Printer Setup' : 'Your Setup'}
                  </span>
                  <div className="flex-1 h-px bg-[#3a3a3a]" />
                </div>
              </div>

              {/* Setup Navigation */}
              {setupNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Link to={item.path} onClick={() => setSidebarOpen(false)}>
                        <div
                          data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                            isActive
                              ? 'text-[#e67e22] bg-[#e67e22]/10 border-l-2 border-[#e67e22]'
                              : item.isAdmin
                                ? 'text-[#f39c12] hover:text-[#e67e22] hover:bg-[#e67e22]/10 border-l-2 border-transparent hover:border-[#e67e22]'
                                : item.highlight
                                  ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 border-l-2 border-emerald-500/50'
                                  : 'text-[#a3a3a3] hover:text-white hover:bg-[#2c2c2c]'
                          }`}
                        >
                          <Icon className={`w-3.5 h-3.5 shrink-0 ${item.isAdmin ? 'text-[#f39c12]' : item.highlight ? 'text-emerald-400' : ''}`} />
                          <span>{item.label}</span>
                          {isActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                        </div>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-[#2c2c2c] text-white border-[#3a3a3a]">
                      <p className="text-xs">{item.tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}

              {/* Divider - Tools & Resources */}
              <div className="pt-4 pb-2">
                <div className="flex items-center gap-2 px-3">
                  <div className="flex-1 h-px bg-[#3a3a3a]" />
                  <span className="text-[10px] uppercase tracking-wider text-[#737373] font-medium">
                    {is3DPrinting ? '3D Printing Tools' : isLaserCutting ? 'Laser Tools' : 'CNC Tools'}
                  </span>
                  <div className="flex-1 h-px bg-[#3a3a3a]" />
                </div>
              </div>

              {/* Main Navigation */}
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const hasChildren = item.children && item.children.length > 0;
                const isChildActive = hasChildren && item.children.some(c =>
                  location.pathname === c.path || location.pathname.startsWith(c.path + '/')
                );
                const isActive = location.pathname === item.path ||
                  location.pathname.startsWith(item.path + '/') ||
                  isChildActive;
                const activeClass = (is3DPrinting || isLaserCutting)
                  ? 'text-orange-500 bg-orange-500/10 border-l-2 border-orange-500'
                  : 'text-[#e67e22] bg-[#e67e22]/10 border-l-2 border-[#e67e22]';
                const highlightClass = item.highlight
                  ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 border-l-2 border-emerald-500/50'
                  : 'text-[#a3a3a3] hover:text-white hover:bg-[#2c2c2c]';
                return (
                  <div key={item.path}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link to={item.path} onClick={() => setSidebarOpen(false)}>
                          <div
                            data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              isActive ? activeClass : highlightClass
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5 shrink-0" />
                            <span>{item.label}</span>
                            {isActive && !hasChildren && <ChevronRight className="w-3 h-3 ml-auto" />}
                          </div>
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="bg-[#2c2c2c] text-white border-[#3a3a3a]">
                        <p className="text-xs">{item.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>

                    {hasChildren && (
                      <div className="ml-4 pl-3 border-l border-[#3a3a3a]">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildItemActive = location.pathname === child.path ||
                            location.pathname.startsWith(child.path + '/');
                          const childActiveClass = (is3DPrinting || isLaserCutting)
                            ? 'text-orange-400 bg-orange-500/10 border-l-2 border-orange-400'
                            : 'text-[#e67e22] bg-[#e67e22]/10 border-l-2 border-[#e67e22]';
                          return (
                            <Tooltip key={child.path}>
                              <TooltipTrigger asChild>
                                <Link to={child.path} onClick={() => setSidebarOpen(false)}>
                                  <div
                                    data-testid={`sidebar-${child.label.toLowerCase().replace(/\s+/g, '-').replace(/[()]/g, '')}`}
                                    className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-sm transition-all ${
                                      isChildItemActive ? childActiveClass : 'text-[#737373] hover:text-white hover:bg-[#2c2c2c]'
                                    }`}
                                  >
                                    <ChildIcon className="w-3 h-3 shrink-0" />
                                    <span>{child.label}</span>
                                    {isChildItemActive && <ChevronRight className="w-2.5 h-2.5 ml-auto" />}
                                  </div>
                                </Link>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="bg-[#2c2c2c] text-white border-[#3a3a3a]">
                                <p className="text-xs">{child.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </TooltipProvider>

          {/* Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#3a3a3a]">
            <div className="text-xs text-[#737373]">
              {is3DPrinting ? `3D Printing Edition v${APP_VERSION}` : isLaserCutting ? `Laser Cutting Edition v${APP_VERSION}` : `CNC Router Edition v${APP_VERSION}`}
            </div>
          </div>
        </aside>

        {/* Main Content - with left margin for fixed sidebar on desktop */}
        <main className="flex-1 p-4 lg:p-6 min-h-[calc(100vh-15rem)] bg-[#1a1a1a] lg:ml-64">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Floating Feedback Button for logged-in users */}
      <FeedbackButton />
    </div>
  );
};

export default Layout;
